import { useAppContext } from '../../context/AppContext';
import { mattergenChunkOptions } from './mattergenJobs';
import { useModelPolicy } from '../../hooks/useCapability';

// The built-in checkpoints the override menu lists. Refine restores any of the 9 (spec 0.2),
// so a bound checkpoint outside this list is shown as an extra option, never as "auto".
const LISTED_CHECKPOINTS = ['mattergen_base', 'chemical_system', 'dft_band_gap', 'ml_bulk_modulus', 'chemical_system_energy_above_hull'];

// The candidate-count options below (docs/NOTES.md, "Candidate count: base-8 doubling pattern").
const CANDIDATE_LADDER = [8, 16, 32, 64, 128, 256, 512, 1024];

export default function MattergenForm({
  elements,
  setElements,
  elementsOut,
  setElementsOut,
  dimensionality,
  setDimensionality,
  structureHints,
  setStructureHints,
  pretrainedOverride,
  setPretrainedOverride,
  property,
  setProperty,
  propertyValue,
  setPropertyValue,
  guidance,
  setGuidance,
  recordTraj,
  setRecordTraj,
  chunkSize,
  setChunkSize,
  numCandidates,
  setNumCandidates,
  errors = {},
}) {
  const { customModels: allCustomModels = [], manualMGCustomModelId = null, setManualMGCustomModelId, planLimits } = useAppContext() || {};
  // 0.7: chunk size is a base-8 menu capped by the plan's per-run max. A bound value that
  // is off the menu (an old state value, or a plan downgrade) stays visible as a disabled
  // option, so the select never shows a different number than the one validation sees.
  const chunkOptions = mattergenChunkOptions(planLimits?.maxCandidatesPerRun);
  const chunkOnMenu = chunkOptions.includes(Number(chunkSize));
  // MatterGen's manual picker must only ever offer mattergen custom models; an fm4m/gt4sd
  // model must never be selectable here (selection is family-scoped end to end; see
  // manualFormToRows in src/ai/rows/manualRows.js).
  // Settled decision 5 (Part 1a, D16): no custom model is offered while MatterGen itself is
  // disabled or weighted to zero.
  const policy = useModelPolicy();
  const mattergenRunnable = !!policy.mattergen?.enabled && policy.mattergen.weight > 0;
  const customModels = mattergenRunnable ? allCustomModels.filter((m) => m.family === 'mattergen') : [];
  // Part 1a (1.7): the checkpoint menu is locked only while the pick is a model the org still
  // has verified. A stale pick never locks it; prepareRows rejects that row at Run instead.
  const pickFound = !!manualMGCustomModelId && customModels.some((m) => m.id === manualMGCustomModelId);
  const selectDark = { background: '#0a1120', border: '1px solid #1e3a5f', borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 13 };

  const onSelectCustomModel = (e) => {
    const id = e.target.value || null;
    setManualMGCustomModelId?.(id);
    if (id) setPretrainedOverride(''); // custom model wins — clear the built-in variant override
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {customModels.length > 0 && (
        <div>
          <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>Custom model</p>
          <select style={{ ...selectDark, width: '100%' }} value={manualMGCustomModelId || ''} onChange={onSelectCustomModel}>
            <option value="">None (use built-in variant below)</option>
            {customModels.map((m) => (
              <option key={m.id} value={m.id}>{m.name} ({m.mode})</option>
            ))}
          </select>
        </div>
      )}
      <select value={pretrainedOverride} onChange={(e) => setPretrainedOverride(e.target.value)} disabled={pickFound}>
        <option value="">auto (recommended)</option>
        <option value="mattergen_base">mattergen_base</option>
        <option value="chemical_system">chemical_system</option>
        <option value="dft_band_gap">dft_band_gap</option>
        <option value="ml_bulk_modulus">ml_bulk_modulus</option>
        <option value="chemical_system_energy_above_hull">chemical_system_energy_above_hull</option>
        {pretrainedOverride && !LISTED_CHECKPOINTS.includes(pretrainedOverride) && (
          <option value={pretrainedOverride}>{pretrainedOverride}</option>
        )}
      </select>
      <input value={elements} onChange={(e) => setElements(e.target.value)} placeholder="Elements included (comma-separated, e.g. Li,Fe,O)" style={{ borderColor: errors.elements ? '#ff6b6b' : undefined }} />
      {errors.elements && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{errors.elements}</div>}
      <input value={elementsOut} onChange={(e) => setElementsOut(e.target.value)} placeholder="Elements excluded (comma-separated)" style={{ borderColor: errors.elementsOut ? '#ff6b6b' : undefined }} />
      {errors.elementsOut && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{errors.elementsOut}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={property} onChange={(e) => setProperty(e.target.value)}>
          <option value="">No target</option>
          <option value="energy_above_hull">energy_above_hull</option>
          <option value="band_gap">band_gap</option>
          <option value="bulk_modulus">bulk_modulus</option>
        </select>
        <input type="number" value={propertyValue} onChange={(e) => setPropertyValue(e.target.value)} placeholder="Target value" style={{ borderColor: errors.propertyValue ? '#ff6b6b' : undefined }} />
      </div>
      {errors.propertyValue && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{errors.propertyValue}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select value={dimensionality} onChange={(e) => setDimensionality(e.target.value)}>
          <option value="unknown">dimensionality: unknown</option>
          <option value="1D">1D</option>
          <option value="2D">2D</option>
          <option value="3D">3D</option>
          <option value="bulk">bulk</option>
          <option value="film">film</option>
        </select>
        <input value={structureHints} onChange={(e) => setStructureHints(e.target.value)} placeholder="Structure hints (comma-separated: perovskite, spinel, hcp, ...)" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="number" value={guidance} onChange={(e) => setGuidance(e.target.value)} placeholder="diffusion guidance factor (e.g. 2.0)" style={{ borderColor: errors.guidance ? '#ff6b6b' : undefined }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={recordTraj} onChange={(e) => setRecordTraj(e.target.checked)} /> record trajectories
        </label>
      </div>
      {errors.guidance && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{errors.guidance}</div>}
      <select aria-label="Candidates" value={numCandidates} onChange={(e) => setNumCandidates(e.target.value)}>
        {/* Spec 0.7: Refine can restore an off-ladder run total (a router split such as 22); show it as itself. */}
        {!CANDIDATE_LADDER.includes(Number(numCandidates)) && Number(numCandidates) > 0 && (
          <option value={String(numCandidates)}>{`${numCandidates} candidates`}</option>
        )}
        <option value="8">8 candidates</option>
        <option value="16">16 candidates</option>
        <option value="32">32 candidates</option>
        <option value="64">64 candidates</option>
        <option value="128">128 candidates</option>
        <option value="256">256 candidates</option>
        <option value="512">512 candidates</option>
        <option value="1024">1,024 candidates</option>
      </select>
      {errors.numCandidates && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{errors.numCandidates}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>Chunk size (candidates per job)</span>
        <select
          aria-label="Chunk size"
          value={chunkSize}
          onChange={(e) => setChunkSize(Number(e.target.value))}
          style={{ borderColor: errors.chunkSize ? '#ff6b6b' : undefined }}
        >
          {!chunkOnMenu && <option value={String(chunkSize)} disabled>{`${chunkSize} (not available)`}</option>}
          {chunkOptions.map((c) => <option key={c} value={String(c)}>{c}</option>)}
        </select>
      </div>
      {errors.chunkSize && <div style={{ color: '#ff6b6b', fontSize: 12 }}>{errors.chunkSize}</div>}
    </div>
  );
}
